// frontend/src/PatientForm.js
import React, { useEffect, useState } from 'react';
import axios from './axiosConfig';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './PatientForm.css';
import DateBox from './DateBox';

/* Helpers to normalize inbound & keep ISO */
function anyToISO(s) {
  if (!s) return '';
  const v = String(s).trim();
  let m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = v.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[1]}-${m[2]}`;
  return '';
}

const PatientForm = () => {
  const { t, i18n } = useTranslation();
  const isZh = String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh');
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [patient, setPatient] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: 'female',
    distributors_mrn: '',
    doc_number: null,
  });

  const [error, setError] = useState('');
  const [dobError, setDobError] = useState(''); // DOB-specific error

  // Location-related state
  const [userLocations, setUserLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [locationName, setLocationName] = useState('');

  const hasMultipleLocations = userLocations.length > 1;
  const singleLocation = userLocations.length === 1 ? userLocations[0] : null;

  const RequiredAsterisk = () => (
    <span style={{ color: '#c0392b', marginLeft: 6 }} aria-hidden="true">
      *
    </span>
  );

  // ---------- LOAD USER LOCATIONS FOR ADD MODE ----------
  useEffect(() => {
    if (isEdit) return; // Edit mode uses the patient’s own service_center

    const applyLocations = (locs) => {
      if (!Array.isArray(locs)) locs = [];
      // Only keep object-like entries (ignore plain IDs)
      locs = locs.filter((l) => l && typeof l === 'object');
      setUserLocations(locs);

      if (locs.length > 0) {
        const first = locs[0];
        setSelectedLocationId(first.id ?? '');
        setLocationName(first.name || first.branch_name || '');
      } else {
        setSelectedLocationId('');
        setLocationName('');
      }
    };

    // 1) Try localStorage (set elsewhere, e.g. Patients list)
    const storedRaw = localStorage.getItem('userLocations');
    if (storedRaw) {
      try {
        const stored = JSON.parse(storedRaw);
        applyLocations(stored);
      } catch {
        // ignore parse errors
      }
    }

    // 2) Fresh pull from auth/whoami/
    axios
      .get('auth/whoami/')
      .then((res) => {
        // Prefer the detailed object list first
        let locs =
          res.data?.locations_detail ||
          res.data?.accessible_locations ||
          res.data?.locations ||
          [];

        // If it's actually just an ID array (numbers/strings), ignore it here
        if (Array.isArray(locs) && locs.length > 0 && typeof locs[0] !== 'object') {
          locs = [];
        }

        applyLocations(locs);

        // Keep localStorage in sync for other components
        if (locs.length > 0) {
          localStorage.setItem('userLocations', JSON.stringify(locs));
        }
      })
      .catch(() => {
        // If auth/whoami/ fails, we just fall back to whatever we got from localStorage
      });
  }, [isEdit]);

  // ---------- LOAD PATIENT FOR EDIT MODE ----------
  useEffect(() => {
    if (!isEdit) return;

    axios
      .get(`patients/${id}/`)
      .then((res) => {
        const data = res.data || {};
        const {
          first_name,
          last_name,
          date_of_birth,
          gender,
          distributors_mrn,
          doc_number,
        } = data;

        const normalizedGender = gender === 'male' || gender === 'female' ? gender : 'female';

        setPatient({
          first_name: first_name || '',
          last_name: last_name || '',
          date_of_birth: anyToISO(date_of_birth || ''),
          gender: normalizedGender,
          distributors_mrn: distributors_mrn || '',
          doc_number: doc_number || null,
        });

        // patient’s own service center name
        const svcName =
          (data.service_center && data.service_center.name) ||
          (data.service_center_detail && data.service_center_detail.name) ||
          data.service_center_name ||
          '';
        if (svcName) setLocationName(svcName);
      })
      .catch((err) => {
        console.error(t('error.fetchPatient'), err);
        setError(t('error.loadPatient'));
      });
  }, [id, isEdit, t]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = value;
    if (type === 'date' && value === '') newValue = null;
    setPatient((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : newValue,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // DOB REQUIRED on CREATE
    if (!isEdit && !patient.date_of_birth) {
      setDobError(t('error.dobRequired', 'Date of Birth is required.'));
      return;
    }
    setDobError('');

    const payload = {
      first_name: patient.first_name,
      last_name: patient.last_name,
      date_of_birth: patient.date_of_birth || null,
      gender: patient.gender === 'male' || patient.gender === 'female' ? patient.gender : 'female',
      distributors_mrn: patient.distributors_mrn || '',
    };

    // Location is chosen only when creating a patient. In edit mode the location is
    // intentionally read-only, so do not send a null service_center back to Django.
    if (!isEdit) payload.service_center = selectedLocationId || null;

    // PATCH on edit preserves Django fields that are not exposed on this form
    // (DSI date, DOC number, fit date, special-case state, etc.).
    const action = isEdit ? axios.patch(`patients/${id}/`, payload) : axios.post('patients/', payload);

    action
      .then(() => navigate('/dashboard/patients'))
      .catch((err) => {
        console.error(isEdit ? t('error.updatePatient') : t('error.addPatient'), err);

        // If backend sends a field error for DOB, show it nicely
        const dobMsg =
          err?.response?.data?.date_of_birth?.[0] ||
          err?.response?.data?.date_of_birth ||
          null;

        if (dobMsg) {
          setDobError(isZh ? t('error.dobInvalid') : String(dobMsg));
          return;
        }

        const data = err?.response?.data;
        const backendDetail =
          data?.detail ||
          data?.non_field_errors?.[0] ||
          (typeof data === 'string' ? data : null) ||
          (data && typeof data === 'object'
            ? Object.entries(data)
                .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                .join(' | ')
            : null);

        setError((!isZh && backendDetail) ? backendDetail : (isEdit ? t('error.failedUpdatePatient') : t('error.failedAddPatient')));
      });
  };

  // ---------- RENDER SERVICE CENTER FIELD ----------
  const renderServiceCenterField = () => {
    // EDIT MODE: show patient’s service center from Django (read-only)
    if (isEdit) {
      return (
        <div className="pfm-field">
          <label>{t('serviceCenter')}</label>
          <div className="pfm-inline">{locationName || t('nA')}</div>
        </div>
      );
    }

    // ADD MODE: single-location user -> default + locked
    if (singleLocation) {
      return (
        <>
          <div className="pfm-field">
            <label>{t('serviceCenter')}</label>
            <div className="pfm-inline">
              {singleLocation.name || singleLocation.branch_name || t('nA')}
            </div>
          </div>
          {/* No editable field; Django will set service_center to this location */}
        </>
      );
    }

    // ADD MODE: multi-location user -> dropdown (UI only)
    if (hasMultipleLocations) {
      return (
        <div className="pfm-field">
          <label>{t('serviceCenter')}</label>
          <select
            className="pfm-select"
            name="service_center"
            value={selectedLocationId || ''}
            onChange={(e) => setSelectedLocationId(e.target.value)}
          >
            {userLocations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name || loc.branch_name || loc.address_line1 || loc.id}
              </option>
            ))}
          </select>
        </div>
      );
    }

    // No locations known (should be rare; e.g., misconfigured user)
    return (
      <div className="pfm-field">
        <label>{t('serviceCenter')}</label>
        <div className="pfm-inline">{t('nA')}</div>
      </div>
    );
  };

  const createDobMissing = !isEdit && !patient.date_of_birth;

  return (
    <div className="pfm-container">
      <h2 className="pfm-title">{isEdit ? t('editPatient') : t('addPatient')}</h2>

      {error && <div className="pfm-error">{error}</div>}

      <form onSubmit={handleSubmit} className="pfm-form">
        <div className="pfm-field">
          <label>
            {t('firstName')}
            <RequiredAsterisk />
          </label>
          <input
            className="pfm-input"
            type="text"
            name="first_name"
            value={patient.first_name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="pfm-field">
          <label>
            {t('lastName')}
            <RequiredAsterisk />
          </label>
          <input
            className="pfm-input"
            type="text"
            name="last_name"
            value={patient.last_name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="pfm-field">
          <label>
            {t('dateOfBirth') + ' (YYYY/MM/DD)'}
            {!isEdit && <RequiredAsterisk />}
          </label>

          <DateBox
            label=""
            valueISO={patient.date_of_birth}
            onChangeISO={(iso) => {
              setPatient((prev) => ({ ...prev, date_of_birth: iso }));
              if (iso) setDobError('');
            }}
            placeholder={t('date.formatYMD')}
            name="date_of_birth"
          />

          {dobError && (
            <div className="pfm-error" style={{ marginTop: 6 }}>
              {dobError}
            </div>
          )}
        </div>

        <div className="pfm-field">
          <label>{t('docNumber')}</label>
          <div className="pfm-inline">
            <strong>{patient.doc_number || t('nA')}</strong>
          </div>
        </div>

        <div className="pfm-field">
          <label>{t('distributorsMRN')}</label>
          <input
            className="pfm-input"
            type="text"
            name="distributors_mrn"
            value={patient.distributors_mrn || ''}
            onChange={handleChange}
          />
        </div>

        {renderServiceCenterField()}

        <div className="pfm-field">
          <label>{t('gender')}</label>
          <select
            className="pfm-select"
            name="gender"
            value={patient.gender === 'male' || patient.gender === 'female' ? patient.gender : 'female'}
            onChange={handleChange}
          >
            <option value="male">{t('male')}</option>
            <option value="female">{t('female')}</option>
          </select>
        </div>

        <div className="pfm-actions">
          <button
            type="submit"
            className="pfm-btn"
            disabled={createDobMissing}
            title={createDobMissing ? t('error.dobRequired', 'Date of Birth is required.') : undefined}
          >
            {isEdit ? t('updatePatient') : t('addPatient')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PatientForm;
